import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Camera, User, Mail, Phone, Lock, Save, Loader2, Eye, EyeOff, Bell, BellOff } from 'lucide-react';
import PrimaryButton from '../components/ui/PrimaryButton';
import { useAuth } from '../contexts/AuthContext';
import { useUsers } from '@/contexts/UsersContext';
import { toast } from 'sonner';
import {
    validateEmail,
    validatePhone,
    validateName,
    validatePassword,
    validatePasswordConfirmation
} from '../lib/validator';
import AvatarSelectionModal from '../components/modals/AvatarSelectionModal';
import CountryPhoneInput from '../components/ui/CountryPhoneInput';
import { getInitials } from '@/lib/avatarUtils';
import { useEvents } from '@/contexts/EventsContext';

interface ProfileData {
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    avatar: string | null;
    role: string;
    description: string | null;
}

const Profile: React.FC = () => {
    const { t } = useTranslation();
    const { user, changePassword } = useAuth();
    const { updateUser } = useUsers();
    const { bookings, getPerformanceById } = useEvents();

    // States UI
    // Removed isEditing state as fields are always editable
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [showAvatarSelection, setShowAvatarSelection] = useState(false);

    // States Password Visibility
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Form State Profile
    const [newProfile, setNewProfile] = useState<ProfileData>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        avatar: '001',
        role: 'user',
        description: ''
    });

    // Form State Password
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Notifications toggle state
    const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);
    const [isTogglingNotifications, setIsTogglingNotifications] = useState(false);

    // Load user data
    useEffect(() => {
        if (user) {
            setNewProfile({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || '',
                avatar: user.avatar || '001',
                role: user.role || 'user',
                description: user.description || ''
            });
            setNotificationsEnabled(user.notifications_enabled ?? false);
        }
    }, [user]);

    // --- Logic Functions ---

    const handleAvatarSelect = async (avatarId: string) => {
        if (!user) return;
        try {
            setIsUploadingAvatar(true);
            const result = await updateUser(user.id, { avatar: avatarId });

            if (!result.success) throw new Error(result.error || 'Errore durante l\'aggiornamento dell\'avatar');

            setNewProfile(prev => ({ ...prev, avatar: avatarId }));
            toast.success('Avatar aggiornato con successo');
            setShowAvatarSelection(false);
        } catch (err) {
            console.error('Errore durante la selezione dell\'avatar:', err);
            toast.error('Errore durante la selezione dell\'avatar');
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    const validateProfileInfo = () => {
        if (!newProfile.first_name?.trim()) return 'Nome richiesto';
        if (!newProfile.last_name?.trim()) return 'Cognome richiesto';
        return null;
    };

    const handleSaveProfile = async () => {
        if (!user) return;

        const basicError = validateProfileInfo();
        if (basicError) {
            toast.error(basicError);
            return;
        }

        const firstNameValidation = validateName(newProfile.first_name, 'Nome');
        if (!firstNameValidation.isValid) {
            toast.error(firstNameValidation.message);
            return;
        }

        const lastNameValidation = validateName(newProfile.last_name, 'Cognome');
        if (!lastNameValidation.isValid) {
            toast.error(lastNameValidation.message);
            return;
        }

        if (newProfile.phone) {
            const phoneValidation = validatePhone(newProfile.phone);
            if (!phoneValidation.isValid) {
                toast.error(phoneValidation.message);
                return;
            }
        }

        setIsSaving(true);
        try {
            const updatedProfile = {
                ...newProfile,
                phone: newProfile.phone || null,
                description: newProfile.description || null
            };

            const result = await updateUser(user.id, updatedProfile);
            if (result.success) {
                toast.success('Profilo aggiornato con successo!');
            } else {
                toast.error(result.error || 'Errore durante l\'aggiornamento del profilo');
            }
        } catch (error) {
            console.error('Errore durante l\'aggiornamento del profilo:', error);
            toast.error('Errore durante l\'aggiornamento del profilo');
        } finally {
            setIsSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            toast.error('Compila tutti i campi password');
            return;
        }
        const passwordValidation = validatePassword(newPassword);
        if (!passwordValidation.isValid) {
            toast.error(passwordValidation.message);
            return;
        }
        const confirmPasswordValidation = validatePasswordConfirmation(newPassword, confirmPassword);
        if (!confirmPasswordValidation.isValid) {
            toast.error(confirmPasswordValidation.message);
            return;
        }

        setIsChangingPassword(true);
        try {
            const result = await changePassword(currentPassword, newPassword);
            if (result.success) {
                toast.success('Password cambiata con successo!');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                toast.error(result.error || 'Errore durante il cambio password');
            }
        } catch (error) {
            toast.error('Errore generico cambio password');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleToggleNotifications = async () => {
        if (!user || isTogglingNotifications) return;
        const newValue = !notificationsEnabled;
        setNotificationsEnabled(newValue);
        setIsTogglingNotifications(true);
        try {
            const result = await updateUser(user.id, { notifications_enabled: newValue });
            if (!result.success) {
                setNotificationsEnabled(!newValue);
                toast.error(result.error || 'Errore notifiche');
            }
        } catch {
            setNotificationsEnabled(!newValue);
            toast.error('Errore notifiche');
        } finally {
            setIsTogglingNotifications(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -10 },
        visible: { opacity: 1, x: 0 }
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl mx-auto space-y-8 pb-12"
        >
            <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
                <p className="text-gray-500 mt-1">{t('profile.subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Column: Avatar & Quick Stats */}
                <motion.div variants={itemVariants} className="space-y-6">
                    <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm flex flex-col items-center text-center relative group">
                        {/* Avatar a iniziali (stile Gmail) — sostituisce la foto profilo */}
                        <div className="w-32 h-32 rounded-full mb-4 bg-gradient-to-tr from-clip-500 to-orange-400 flex items-center justify-center text-white text-4xl font-bold border-4 border-white shadow-lg">
                            {getInitials(`${user.first_name ?? ''} ${user.last_name ?? ''}`)}
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 mb-3">
                            {user.first_name} {user.last_name}
                        </h2>

                        <div className="flex gap-2 text-xs font-bold text-clip-600 bg-clip-50 px-3 py-1 rounded-full uppercase tracking-wide">
                            {user.role === 'admin' ? 'Admin' : 'Staff'}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-6 text-white shadow-lg">
                        <h3 className="font-bold mb-4 opacity-90">{t('profile.personal_stats')}</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center pb-2 border-b border-white/10">
                                <span className="text-sm text-gray-400">{t('profile.today_appts')}</span>
                                <span className="font-bold text-lg">{bookings.filter((b: any) => b.date === new Date().toISOString().split('T')[0]).length}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-400">{t('profile.hours_worked')}</span>
                                <span className="font-bold text-lg">
                                    {Math.round(bookings
                                        .filter((b: any) => b.date === new Date().toISOString().split('T')[0])
                                        .reduce((acc: number, b: any) => {
                                            const perf = getPerformanceById(b.performance_id);
                                            return acc + (perf?.duration || 0);
                                        }, 0) / 60)}h
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Right Column: Forms */}
                <motion.div variants={itemVariants} className="md:col-span-2 space-y-8">

                    {/* Personal Info */}
                    <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <User size={20} className="text-clip-500" /> {t('profile.personal_data')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('customers.modal.name')}</label>
                                <input
                                    type="text"
                                    value={newProfile.first_name}
                                    onChange={(e) => setNewProfile({ ...newProfile, first_name: e.target.value })}
                                    className="w-full p-3 rounded-xl border outline-none font-medium transition-all bg-white focus:ring-2 focus:ring-clip-200"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('customers.modal.surname')}</label>
                                <input
                                    type="text"
                                    value={newProfile.last_name}
                                    onChange={(e) => setNewProfile({ ...newProfile, last_name: e.target.value })}
                                    className="w-full p-3 rounded-xl border outline-none font-medium transition-all bg-white focus:ring-2 focus:ring-clip-200"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('customers.modal.email')}</label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="email"
                                        value={newProfile.email}
                                        onChange={(e) => setNewProfile({ ...newProfile, email: e.target.value })}
                                        className="w-full pl-10 p-3 rounded-xl border outline-none font-medium transition-all bg-gray-50 border-transparent text-gray-500 cursor-not-allowed"
                                        readOnly={true}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('customers.modal.phone')}</label>
                                <div className="relative">
                                    <CountryPhoneInput
                                        value={newProfile.phone || ''}
                                        onChange={(val) => setNewProfile({ ...newProfile, phone: val })}
                                        placeholder="+39 333 123 4567"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <PrimaryButton
                                label={isSaving ? "Salvataggio..." : t('customers.modal.save_changes')}
                                icon={isSaving ? Loader2 : Save}
                                onClick={handleSaveProfile}
                                disabled={isSaving}
                                className={isSaving ? "opacity-70 cursor-not-allowed" : ""}
                            />
                        </div>
                    </div>
                    {/* Security Section */}

                    <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <Lock size={20} className="text-clip-500" /> {t('profile.security')}
                        </h3>

                        <div className="space-y-4">
                            {/* Current Password */}
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Password Attuale</label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        className="w-full p-3 rounded-xl border border-gray-200 outline-none font-medium focus:ring-2 focus:ring-clip-200 focus:border-clip-200 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* New Password */}
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Nuova Password</label>
                                    <div className="relative">
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full p-3 rounded-xl border border-gray-200 outline-none font-medium focus:ring-2 focus:ring-clip-200 focus:border-clip-200 transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Conferma Password</label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full p-3 rounded-xl border border-gray-200 outline-none font-medium focus:ring-2 focus:ring-clip-200 focus:border-clip-200 transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2 flex justify-end">
                                <PrimaryButton
                                    label={isChangingPassword ? "Aggiornamento..." : "Aggiorna Password"}
                                    onClick={handleChangePassword}
                                    disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
                                    icon={isChangingPassword ? Loader2 : Lock}
                                    className={isChangingPassword || !currentPassword || !newPassword || !confirmPassword ? "opacity-70" : ""}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Notifications Section */}
                    <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <Bell size={20} className="text-clip-500" /> {t('profile.notifications.title')}
                        </h3>
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <div>
                                <p className="font-semibold text-gray-800 text-sm">{t('profile.notifications.appointments_label')}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{t('profile.notifications.appointments_desc')}</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleToggleNotifications}
                                disabled={isTogglingNotifications}
                                className={`relative w-12 h-6 rounded-full transition-all duration-300 flex-shrink-0 disabled:opacity-60 ${notificationsEnabled ? 'bg-clip-500' : 'bg-gray-300'
                                    }`}
                            >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${notificationsEnabled ? 'translate-x-6' : 'translate-x-0'
                                    }`} />
                            </button>
                        </div>
                    </div>

                </motion.div>
            </div >

            <AvatarSelectionModal
                isOpen={showAvatarSelection}
                onClose={() => setShowAvatarSelection(false)}
                currentAvatar={newProfile.avatar || '001'}
                onSelect={handleAvatarSelect}
            />
        </motion.div >
    );
};

export default Profile;